import { useCallback, useState } from 'react';
import type { Asset, FileUpdateChanges, FilesApi } from './types';

export interface FileDeleteState {
    deleteTargets: Asset[];
    isDeleting: boolean;
    deleteError: string | null;
}

export interface FileDeleteActions {
    handleDeleteFile: (asset: Asset) => void;
    handleDeleteSelected: (assets: Record<string, Asset>, selectedAssets: Set<string>) => void;
    confirmDelete: () => Promise<void>;
    cancelDelete: () => void;
    clearDeleteError: () => void;
}

export interface FileRenameState {
    renameTarget: Asset | null;
    isRenaming: boolean;
    renameError: string | null;
}

export interface FileRenameActions {
    handleRenameFile: (asset: Asset) => void;
    confirmRename: (changes: {
        newName?: string;
        fileType?: string;
        metadata?: Record<string, unknown>;
    }) => Promise<void>;
    cancelRename: () => void;
}

export type FileDeleteRenameResult = FileDeleteState &
    FileDeleteActions &
    FileRenameState &
    FileRenameActions;

interface UseFileDeleteRenameOptions {
    patientId: string;
    refreshAssets: () => Promise<void>;
    exitSelection: () => void;
    api: FilesApi;
}

export function useFileDeleteRename({
    patientId,
    refreshAssets,
    exitSelection,
    api
}: UseFileDeleteRenameOptions): FileDeleteRenameResult {
    const [deleteTargets, setDeleteTargets] = useState<Asset[]>([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const [renameTarget, setRenameTarget] = useState<Asset | null>(null);
    const [isRenaming, setIsRenaming] = useState(false);
    const [renameError, setRenameError] = useState<string | null>(null);

    const handleDeleteFile = useCallback((asset: Asset) => {
        setDeleteTargets([asset]);
    }, []);

    const handleDeleteSelected = useCallback(
        (assets: Record<string, Asset>, selectedAssets: Set<string>) => {
            const selectedList = Object.values(assets).filter(
                (a) =>
                    selectedAssets.has(String(a.assetId)) && a.source === 'gcs' && a.patientFileId
            );
            if (selectedList.length === 0) return;
            setDeleteTargets(selectedList);
        },
        []
    );

    const confirmDelete = useCallback(async () => {
        if (!patientId || deleteTargets.length === 0) return;
        setIsDeleting(true);
        setDeleteError(null);
        try {
            const results = await Promise.allSettled(
                deleteTargets
                    .filter((a) => a.patientFileId)
                    .map((a) => api.deleteFile(patientId, a.patientFileId!))
            );
            const failed = results.filter((r) => r.status === 'rejected').length;
            if (failed > 0) {
                setDeleteError(`${failed} file(s) could not be deleted.`);
            }
            exitSelection();
        } finally {
            await refreshAssets();
            setIsDeleting(false);
            setDeleteTargets([]);
        }
    }, [patientId, deleteTargets, refreshAssets, exitSelection, api]);

    const cancelDelete = useCallback(() => {
        if (!isDeleting) setDeleteTargets([]);
    }, [isDeleting]);

    const clearDeleteError = useCallback(() => setDeleteError(null), []);

    const handleRenameFile = useCallback((asset: Asset) => {
        setRenameError(null);
        setRenameTarget(asset);
    }, []);

    const confirmRename = useCallback(
        async (changes: {
            newName?: string;
            fileType?: string;
            metadata?: Record<string, unknown>;
        }) => {
            if (!patientId || !renameTarget?.patientFileId) return;
            // Only send the fields that actually changed; bail out if nothing did.
            const body: FileUpdateChanges = {};
            if (changes.newName !== undefined) body.originalFilename = changes.newName;
            if (changes.fileType !== undefined) body.fileType = changes.fileType;
            if (changes.metadata !== undefined) body.metadata = changes.metadata;
            if (Object.keys(body).length === 0) {
                setRenameTarget(null);
                return;
            }
            setIsRenaming(true);
            setRenameError(null);
            try {
                await api.updateFile(patientId, renameTarget.patientFileId, body);
                await refreshAssets();
                setRenameTarget(null);
            } catch (err: unknown) {
                const errorObj = err as {
                    response?: { data?: { message?: string } };
                    message?: string;
                };
                setRenameError(
                    errorObj?.response?.data?.message ||
                        errorObj?.message ||
                        'Failed to save changes'
                );
            } finally {
                setIsRenaming(false);
            }
        },
        [patientId, renameTarget, refreshAssets, api]
    );

    const cancelRename = useCallback(() => {
        if (!isRenaming) {
            setRenameTarget(null);
            setRenameError(null);
        }
    }, [isRenaming]);

    return {
        deleteTargets,
        isDeleting,
        deleteError,
        handleDeleteFile,
        handleDeleteSelected,
        confirmDelete,
        cancelDelete,
        clearDeleteError,
        renameTarget,
        isRenaming,
        renameError,
        handleRenameFile,
        confirmRename,
        cancelRename
    };
}
